import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PaperAirplaneIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { documentsAPI, ProcessingJob, exportAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { useActivitiesStore, SavedActivity } from '../store/activitiesStore';
import { useTranslation } from 'react-i18next';

// Helper function to generate UUIDs with fallback for browsers that don't support crypto.randomUUID
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// List of major countries
const COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Ireland',
  'Portugal',
  'Greece',
  'Poland',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Japan',
  'China',
  'South Korea',
  'India',
  'Singapore',
  'Malaysia',
  'Thailand',
  'Indonesia',
  'Philippines',
  'Vietnam',
  'Brazil',
  'Mexico',
  'Argentina',
  'Chile',
  'Colombia',
  'Peru',
  'South Africa',
  'Egypt',
  'Nigeria',
  'Kenya',
  'Israel',
  'United Arab Emirates',
  'Saudi Arabia',
  'Turkey',
  'Russia',
  'Ukraine',
  'New Zealand',
].sort();

// Popular topics based on jurisdiction
const DEFAULT_TOPICS = [
  'What are my rights as a tenant?',
  'How do I form an LLC?',
  'What is the small claims court process?',
  'How do I file for a name change?',
  'What should be in a basic will?',
];

const TOPICS_BY_COUNTRY: Record<string, string[]> = {
  'United States': [
    'What are my rights under the Fair Housing Act?',
    'How do I form an LLC in my state?',
    'What is the small claims court limit in my state?',
    'How do I file for divorce?',
    'What are my rights as an employee?',
  ],
  'Canada': [
    'What are tenant rights in my province?',
    'How do I incorporate a business in Canada?',
    'What is the small claims process in my province?',
    'How do I file for divorce in Canada?',
    'What are my employment rights under Canadian law?',
  ],
  'United Kingdom': [
    'What are my rights as a tenant in the UK?',
    'How do I form a limited company?',
    'What is the small claims track process?',
    'How do I apply for a decree absolute?',
    'What are my rights under UK employment law?',
  ],
  'Australia': [
    'What are tenant rights in my state?',
    'How do I register a company with ASIC?',
    'What is the VCAT or small claims process?',
    'How do I apply for divorce in Australia?',
    'What are Fair Work Act protections?',
  ],
};

const TOPICS_BY_US_STATE: Record<string, string[]> = {
  'California': [
    'What are tenant rights under California law?',
    'How do I form an LLC in California?',
    'What is the small claims limit in California?',
    'What are California employment rights?',
    'How do I file for divorce in California?',
  ],
  'Texas': [
    'What are tenant rights in Texas?',
    'How do I form an LLC in Texas?',
    'What is the small claims limit in Texas?',
    'What are Texas at-will employment laws?',
    'How do I file for divorce in Texas?',
  ],
  'New York': [
    'What are NYC rent stabilization laws?',
    'How do I form an LLC in New York?',
    'What is the small claims limit in New York?',
    'What are New York labor law protections?',
    'How do I file for divorce in New York?',
  ],
  'Florida': [
    'What are tenant rights in Florida?',
    'How do I form an LLC in Florida?',
    'What is the small claims limit in Florida?',
    'What are Florida employment laws?',
    'How do I file for divorce in Florida?',
  ],
};

// States/Provinces/Regions by country
const STATES_BY_COUNTRY: Record<string, string[]> = {
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'Canada': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'Australia': [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ],
  'United Kingdom': [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ],
  'Germany': [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse',
    'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate',
    'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
  ],
  'India': [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
    'West Bengal'
  ],
  'Mexico': [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
    'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Mexico City',
    'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
    'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ],
  'Brazil': [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal', 'Espírito Santo',
    'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul', 'Minas Gerais', 'Pará', 'Paraíba',
    'Paraná', 'Pernambuco', 'Piauí', 'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul',
    'Rondônia', 'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins'
  ]
};

interface QuestionResult {
  id: string;
  question: string;
  result: ProcessingJob;
  location: { country: string; state: string; municipality: string };
  timestamp: Date;
}

// Helper function to get dynamic topics based on jurisdiction
const getPopularTopics = (country: string, state: string): string[] => {
  // If US state is selected and has specific topics, use those
  if (country === 'United States' && state && TOPICS_BY_US_STATE[state]) {
    return TOPICS_BY_US_STATE[state];
  }

  // If country has specific topics, use those
  if (country && TOPICS_BY_COUNTRY[country]) {
    return TOPICS_BY_COUNTRY[country];
  }

  // Return default topics
  return DEFAULT_TOPICS;
};

export const LegalQuestions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Form state
  const [activityName, setActivityName] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [question, setQuestion] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [questionHistory, setQuestionHistory] = useState<QuestionResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [lastSavedActivityName, setLastSavedActivityName] = useState('');
  const [lastSavedHistoryLength, setLastSavedHistoryLength] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handle initial query from Dashboard navigation
  useEffect(() => {
    const navState = location.state as { initialQuery?: string; loadActivityId?: string; clearActivity?: boolean } | null;
    if (navState?.initialQuery) {
      setQuestion(navState.initialQuery);
    }
  }, [location.state]);

  // Clear current activity and reset form when navigating without a specific activity to load
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string; clearActivity?: boolean } | null;
    if (!navState?.loadActivityId || navState?.clearActivity) {
      resetForm();
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear state when country changes
  useEffect(() => {
    setState('');
  }, [country]);

  // Track changes for unsaved state
  useEffect(() => {
    const nameChanged = activityName !== lastSavedActivityName;
    const historyChanged = questionHistory.length !== lastSavedHistoryLength;

    if (nameChanged || historyChanged) {
      setHasUnsavedChanges(true);
      setIsSaved(false);
    }
  }, [activityName, questionHistory.length, lastSavedActivityName, lastSavedHistoryLength]);

  // Load activity if navigated from sidebar
  useEffect(() => {
    if (location.state?.loadActivityId) {
      const activity = getActivityById(location.state.loadActivityId);
      if (activity && activity.activityType === 'legal-questions') {
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(
            'You have unsaved changes. Do you want to discard them and load the selected activity?'
          );
          if (!confirmed) return;
        }

        setActivityName(activity.name);
        // Convert old format to new format if needed
        const convertedHistory = activity.actionHistory.map((item: any) => ({
          id: item.id || generateUUID(),
          question: item.question || '',
          result: item.result,
          location: item.location || { country: '', state: '', municipality: '' },
          timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        }));
        setQuestionHistory(convertedHistory);
        setCurrentActivityId(activity.id);
        setLastSavedActivityName(activity.name);
        setLastSavedHistoryLength(activity.actionHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state, getActivityById, hasUnsavedChanges, setCurrentActivityId]);

  // Scroll to top of results when new question is added
  useEffect(() => {
    if (questionHistory.length > 0) {
      resultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [questionHistory.length]);

  const resetForm = () => {
    setCurrentActivityId(null);
    setActivityName('');
    setCountry('');
    setState('');
    setMunicipality('');
    setQuestion('');
    setQuestionHistory([]);
    setLastSavedActivityName('');
    setLastSavedHistoryLength(0);
    setIsSaved(false);
    setHasUnsavedChanges(false);
    setError(null);
  };

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build location context for the prompt
      const locationParts = [];
      if (country) locationParts.push(`Country: ${country}`);
      if (state) locationParts.push(`State/Province/Region: ${state}`);
      if (municipality) locationParts.push(`Municipality/City: ${municipality}`);

      const locationContext = locationParts.length > 0
        ? `\n\nJurisdiction Context:\n${locationParts.join('\n')}\n\nPlease provide information relevant to this jurisdiction.`
        : '';

      const fullQuestion = `${question}${locationContext}`;

      // Use general query endpoint
      const result = await documentsAPI.generalQuery(fullQuestion);

      // Create new history item
      const newHistoryItem: QuestionResult = {
        id: generateUUID(),
        question: question,
        result,
        location: {
          country: country || '',
          state: state || '',
          municipality: municipality || '',
        },
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, newHistoryItem];
      setQuestionHistory(updatedHistory);
      setQuestion('');

      // Auto-save the activity
      let generatedName = activityName;
      if (!generatedName.trim()) {
        try {
          const summaryPrompt = `Summarize the following question in 3-5 words for use as a title: "${question}"`;
          const summaryResult = await documentsAPI.generalQuery(summaryPrompt);
          if (summaryResult.result) {
            generatedName = summaryResult.result
              .replace(/^["']|["']$/g, '')
              .split('\n')[0]
              .substring(0, 50);
            setActivityName(generatedName);
          }
        } catch (error) {
          console.error('Failed to generate activity name:', error);
          generatedName = question.substring(0, 50);
          setActivityName(generatedName);
        }
      }

      if (generatedName.trim()) {
        const savedActivity: SavedActivity = {
          id: currentActivityId || generateUUID(),
          name: generatedName,
          uploadedFiles: [],
          selectedFiles: new Set(),
          actionHistory: updatedHistory.map(item => ({
            action: 'legal-question',
            result: item.result,
            question: item.question,
            location: item.location,
            timestamp: item.timestamp,
          })) as any,
          savedAt: new Date(),
          activityType: 'legal-questions',
        };

        if (currentActivityId) {
          updateActivity(savedActivity);
        } else {
          addActivity(savedActivity);
          setCurrentActivityId(savedActivity.id);
        }

        setLastSavedHistoryLength(updatedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error submitting question:', error);
      setError('Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate export content for a question
  const generateExportContent = (item: QuestionResult): string => {
    const locationParts = [];
    if (item.location.country) locationParts.push(item.location.country);
    if (item.location.state) locationParts.push(item.location.state);
    if (item.location.municipality) locationParts.push(item.location.municipality);
    const locationStr = locationParts.length > 0 ? locationParts.join(', ') : '';

    let content = `Legal Question\n`;
    content += `Date: ${item.timestamp.toLocaleString()}\n`;
    if (locationStr) {
      content += `Jurisdiction: ${locationStr}\n`;
    }
    content += `\n### Question\n${item.question}\n\n`;
    content += `${item.result.result || 'No result available'}\n\n`;
    content += `### Disclaimer\n`;
    content += `This information is provided for general educational purposes only and does not constitute legal advice. `;
    content += `For specific legal matters, please consult with a qualified attorney in your jurisdiction.\n`;

    return content;
  };

  // Handle export for a specific index
  const handleExportForIndex = (index: number) => (format: 'word' | 'pdf' | 'text') => {
    const actualIndex = questionHistory.length - 1 - index;
    const item = questionHistory[actualIndex];
    if (!item) return;
    exportDocument(item, format);
  };

  // Handle print for a specific index
  const handlePrintForIndex = (index: number) => () => {
    const actualIndex = questionHistory.length - 1 - index;
    const item = questionHistory[actualIndex];
    if (!item) return;
    handlePrint(item);
  };

  // Handle print
  const handlePrint = (item: QuestionResult) => {
    const locationParts = [];
    if (item.location.country) locationParts.push(item.location.country);
    if (item.location.state) locationParts.push(item.location.state);
    if (item.location.municipality) locationParts.push(item.location.municipality);
    const locationStr = locationParts.length > 0 ? locationParts.join(', ') : '';

    // Convert markdown to HTML using marked
    const answerHtml = marked(item.result.result || 'No result available', { async: false }) as string;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Legal Question</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { text-align: center; color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1d4ed8; margin-top: 24px; }
          h3 { color: #1e40af; margin-top: 16px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          .location { font-style: italic; color: #6b7280; }
          ul, ol { margin: 8px 0; padding-left: 24px; }
          li { margin-bottom: 6px; }
          p { margin: 8px 0; }
          strong { font-weight: 600; }
          em { font-style: italic; }
          code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
          pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow-x: auto; }
          pre code { background: none; padding: 0; }
          blockquote { border-left: 4px solid #2563eb; margin: 16px 0; padding-left: 16px; color: #666; }
          .answer-content h3 { font-size: 1.1em; margin-top: 20px; margin-bottom: 8px; }
          .answer-content ul, .answer-content ol { margin: 12px 0; }
          .answer-content li { margin-bottom: 8px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #dbeafe; border-radius: 8px; font-size: 12px; color: #1e40af; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Legal Question</h1>
        <div class="meta">
          <p>Date: ${item.timestamp.toLocaleString()}</p>
          ${locationStr ? `<p class="location">Jurisdiction: ${locationStr}</p>` : ''}
        </div>
        <h2>Your Question</h2>
        <p>${item.question}</p>
        <div class="answer-content">${answerHtml}</div>
        <div class="disclaimer">
          <strong>Disclaimer:</strong> This information is provided for general educational purposes only and does not constitute legal advice. For specific legal matters, please consult with a qualified attorney in your jurisdiction.
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Export document to Word, PDF, or Text
  const exportDocument = async (item: QuestionResult, format: 'word' | 'pdf' | 'text') => {
    try {
      const content = generateExportContent(item);
      const title = 'Legal Question';
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `legal-question-${item.id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      // Try File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain',
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File',
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: descriptions[format],
                accept: { [mimeTypes[extension]]: [`.${extension}`] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      // Fallback download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export document. Please try again.');
    }
  };

  const handleDeleteClick = (index: number) => {
    setDeleteTargetIndex(index);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteTargetIndex === null) return;

    const actualIndex = questionHistory.length - 1 - deleteTargetIndex;
    const newHistory = questionHistory.filter((_, idx) => idx !== actualIndex);

    setQuestionHistory(newHistory);
    setShowDeleteDialog(false);
    setDeleteTargetIndex(null);

    // Update the activity in store
    if (currentActivityId) {
      const activity: SavedActivity = {
        id: currentActivityId,
        name: activityName,
        uploadedFiles: [],
        selectedFiles: new Set(),
        actionHistory: newHistory.map(item => ({
          action: 'legal-question',
          result: item.result,
          question: item.question,
          location: item.location,
          timestamp: item.timestamp,
        })) as any,
        savedAt: new Date(),
        activityType: 'legal-questions'
      };
      updateActivity(activity);
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <QuestionMarkCircleIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('legal:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('legal:subtitle', 'Get answers to your legal questions')}
        </p>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Options */}
        <div className="lg:w-1/4 flex flex-col space-y-4 overflow-y-auto">
          {/* Jurisdiction Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Country (optional)
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* State/Province/Region */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              State/Province/Region (optional)
            </label>
            {country && STATES_BY_COUNTRY[country] ? (
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a state/province/region</option>
                {STATES_BY_COUNTRY[country].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder={country ? "Enter state/province/region" : "Select a country first"}
                disabled={!country}
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
              />
            )}
          </div>

          {/* Municipality/City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Municipality/City (optional)
            </label>
            <input
              type="text"
              value={municipality}
              onChange={(e) => setMunicipality(e.target.value)}
              placeholder="e.g., San Francisco"
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Popular Topics - Dynamic based on jurisdiction */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              Popular Topics
              {(country || state) && (
                <span className="font-normal text-xs text-blue-600 dark:text-blue-400 ml-1">
                  ({state || country})
                </span>
              )}
            </h3>
            <div className="space-y-1">
              {getPopularTopics(country, state).map((topic, i) => (
                <button
                  key={i}
                  onClick={() => setQuestion(topic)}
                  className="block w-full text-left text-sm text-blue-700 dark:text-blue-300 hover:underline"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Reset
          </button>
        </div>

        {/* Right Panel - Question & Answers */}
        <div className="lg:w-3/4 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Question Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleSubmitQuestion}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('legal:yourQuestion', 'Your Question')}
              </label>
              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask a legal question..."
                  rows={3}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title="Submit question"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </form>
          </div>

          {/* Question History */}
          <div ref={resultsContainerRef} className="flex-1 overflow-y-auto p-4 space-y-6">
            {questionHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p>No questions yet</p>
                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">Ask a legal question to get started</p>
              </div>
            ) : (
              [...questionHistory].reverse().map((item, reversedIndex) => {
                const locationParts = [];
                if (item.location.country) locationParts.push(item.location.country);
                if (item.location.state) locationParts.push(item.location.state);
                if (item.location.municipality) locationParts.push(item.location.municipality);
                const locationStr = locationParts.length > 0 ? locationParts.join(', ') : '';

                return (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded">
                          Legal Question
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {item.timestamp.toLocaleString()}
                        </p>
                        {locationStr && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-1">
                            Jurisdiction: {locationStr}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <ExportDropdownMenu
                          onExport={handleExportForIndex(reversedIndex)}
                          onPrint={handlePrintForIndex(reversedIndex)}
                          showPrint={true}
                        />
                        <button
                          onClick={() => handleDeleteClick(reversedIndex)}
                          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Your Question:
                      </h4>
                      <p className="text-gray-700 dark:text-gray-200 text-sm italic">
                        {item.question}
                      </p>
                    </div>

                    <div className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-li:text-gray-800 dark:prose-li:text-gray-200 prose-strong:text-gray-900 dark:prose-strong:text-white">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({node, ...props}) => (
                            <a
                              {...props}
                              className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          )
                        }}
                      >
                        {item.result.result || 'No result available'}
                      </ReactMarkdown>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <strong>Disclaimer:</strong> This information is provided for general educational purposes only and does not constitute legal advice. For specific legal matters, please consult with a qualified attorney in your jurisdiction.
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-blue-800 dark:text-blue-200 text-center">
          <strong>Disclaimer:</strong> This information is provided for general educational purposes only and does not constitute legal advice. For specific legal matters, please consult with a qualified attorney in your jurisdiction.
        </p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-gray-100">Processing your question...</span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Delete Question
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this question? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteTargetIndex(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
